import React from 'react';
import { Button } from '../components/ui/button';
import { FileHeart, Heart, Users, Shield, Hospital, CheckCircle2, ArrowRight } from 'lucide-react';

const SupplementPlans = () => {
  const benefits = [
    'Fill gaps left by Medicare or primary insurance coverage',
    'Reduce out-of-pocket medical expenses significantly',
    'Access to a wider network of healthcare providers',
    'Coverage for prescription drugs and medical equipment',
    'Peace of mind with comprehensive healthcare protection',
    'Expert guidance through the selection process'
  ];

  const planTypes = [
    {
      icon: Shield,
      title: 'Accident Insurance',
      description: 'Provides cash benefits for injuries from accidents including fractures, dislocations, burns, and emergency room visits. Helps cover deductibles, copays, and out-of-pocket expenses not covered by your primary insurance.'
    },
    {
      icon: Heart,
      title: 'Cancer Insurance',
      description: 'Specialized coverage that pays cash benefits upon cancer diagnosis and throughout treatment. Helps cover costs like chemotherapy, radiation, experimental treatments, travel expenses, and income loss during recovery.'
    },
    {
      icon: Users,
      title: 'Vision Insurance',
      description: 'Affordable coverage for routine eye exams, prescription glasses, contact lenses, and discounts on corrective eye surgery. Essential for maintaining eye health and managing vision correction costs.'
    },
    {
      icon: Hospital,
      title: 'Critical Care Insurance',
      description: 'Lump-sum payment upon diagnosis of serious conditions like heart attack, stroke, organ failure, or major surgery. Provides financial support when you need it most to focus on recovery without financial stress.'
    },
    {
      icon: FileHeart,
      title: 'Dental Insurance',
      description: 'Comprehensive dental coverage including preventive care (cleanings, exams), basic procedures (fillings, extractions), and major services (crowns, bridges, root canals). Maintains oral health and reduces costly dental bills.'
    },
    {
      icon: Hospital,
      title: 'Hospitalization Insurance',
      description: 'Pays cash benefits directly to you for hospital stays, including daily hospital confinement, ICU admission, ambulance services, and outpatient surgery. Helps cover high deductibles and out-of-pocket hospital costs.'
    },
    {
      icon: Shield,
      title: 'Disability Insurance',
      description: 'Replaces a portion of your income if you become unable to work due to illness or injury. Provides financial stability to cover living expenses, bills, and maintain your lifestyle during recovery periods.'
    }
  ];

  const whyNeed = [
    {
      title: 'Coverage Gaps',
      description: 'Medicare and standard health insurance don\'t cover everything. Supplement plans fill these crucial gaps.'
    },
    {
      title: 'Out-of-Pocket Costs',
      description: 'Reduce or eliminate expensive copays, coinsurance, and deductibles that can add up quickly.'
    },
    {
      title: 'Freedom of Choice',
      description: 'See any doctor or specialist that accepts Medicare without network restrictions.'
    },
    {
      title: 'Budget Protection',
      description: 'Predictable healthcare costs help you budget better and avoid financial surprises.'
    }
  ];

  const whoNeeds = [
    'Seniors on Medicare looking to reduce out-of-pocket costs',
    'Individuals with chronic conditions requiring frequent care',
    'Those who travel frequently and need coverage nationwide',
    'People approaching age 65 and Medicare eligibility',
    'Anyone wanting comprehensive healthcare protection',
    'Families seeking additional coverage beyond employer plans'
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#D0E8F2] to-[#C4E5F4] py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-white p-6 rounded-full shadow-lg">
              <FileHeart className="h-16 w-16 text-[#0B8FD8]" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold mb-6">
            Supplement Plans
          </h1>
          <p className="text-lg md:text-xl text-[#2C3E50] leading-relaxed mb-8">
            Bridge the gap in your healthcare coverage with Medicare supplement plans and additional 
            insurance options that provide comprehensive protection and peace of mind.
          </p>
          <Button 
            className="bg-[#0B8FD8] hover:bg-[#0973B0] text-white px-8 py-4 text-lg rounded-md transition-all duration-300 shadow-lg hover:shadow-xl"
            onClick={() => window.open('https://go.sbrooksassociatesllc.com/bookwithshannon', '_blank')}
          >
            Get Your Free Supplement Plan Quote
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* What Are Supplement Plans */}
      <section className="bg-white py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold text-center mb-12">
            What Are Supplement Plans?
          </h2>
          
          <div className="bg-gradient-to-br from-[#D0E8F2] to-[#C4E5F4] rounded-3xl p-8 md:p-12">
            <p className="text-lg text-[#2C3E50] leading-relaxed mb-6">
              Supplement plans, also known as Medigap or supplemental insurance, are designed to work alongside 
              your existing health coverage to help pay for healthcare costs that your primary insurance doesn't cover. 
              These plans help reduce or eliminate out-of-pocket expenses like copayments, coinsurance, and deductibles.
            </p>
            <p className="text-lg text-[#2C3E50] leading-relaxed">
              Whether you're on Medicare or have private insurance, supplement plans provide an extra layer of financial 
              protection, ensuring you're not burdened with unexpected medical bills. They give you the freedom to choose 
              your healthcare providers and the peace of mind that comes with comprehensive coverage.
            </p>
          </div>
        </div>
      </section>

      {/* Plan Types Section */}
      <section className="bg-gradient-to-br from-[#D0E8F2] to-[#C4E5F4] py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold text-center mb-6">
            Healthcare Supplement Plans Available
          </h2>
          <p className="text-lg text-[#2C3E50] text-center max-w-3xl mx-auto mb-16">
            We offer a comprehensive range of supplement plans to protect you and your family from unexpected healthcare costs.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {planTypes.map((plan, index) => {
              const Icon = plan.icon;
              return (
                <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="bg-[#C4E5F4] p-3 rounded-lg">
                      <Icon className="h-8 w-8 text-[#0B8FD8]" />
                    </div>
                  </div>
                  <h3 className="text-xl font-serif text-[#0A2540] font-bold">
                    {plan.title}
                  </h3>
                  <p className="text-base text-[#2C3E50] leading-relaxed">
                    {plan.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why You Need Section */}
      <section className="bg-white py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold text-center mb-16">
            Why You Need a Supplement Plan
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyNeed.map((reason, index) => (
              <div key={index} className="bg-[#C4E5F4] rounded-xl p-6 text-center space-y-4">
                <h3 className="text-xl font-serif text-[#0A2540] font-bold">
                  {reason.title}
                </h3>
                <p className="text-base text-[#2C3E50]">
                  {reason.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Needs Section */}
      <section className="bg-gradient-to-br from-[#D0E8F2] to-[#C4E5F4] py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold text-center mb-12">
            Who Needs Supplement Plans?
          </h2>
          
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {whoNeeds.map((person, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <CheckCircle2 className="h-6 w-6 text-[#0B8FD8] flex-shrink-0 mt-1" />
                  <p className="text-base text-[#2C3E50] font-medium">{person}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-white py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold text-center mb-12">
            Benefits of Supplement Plans
          </h2>
          
          <div className="bg-gradient-to-br from-[#D0E8F2] to-[#C4E5F4] rounded-3xl p-8 md:p-12">
            <div className="space-y-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <CheckCircle2 className="h-6 w-6 text-[#0B8FD8] flex-shrink-0 mt-1" />
                  <p className="text-lg text-[#2C3E50]">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How We Help Section */}
      <section className="bg-gradient-to-br from-[#D0E8F2] to-[#C4E5F4] py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A2540] font-bold text-center mb-16">
            How We Help You Choose
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 text-center space-y-4">
              <div className="text-5xl font-bold text-[#0B8FD8]">1</div>
              <h3 className="text-xl font-serif text-[#0A2540] font-bold">
                Assess Your Needs
              </h3>
              <p className="text-base text-[#2C3E50]">
                We evaluate your current coverage, health status, and budget to understand your unique needs.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 text-center space-y-4">
              <div className="text-5xl font-bold text-[#0B8FD8]">2</div>
              <h3 className="text-xl font-serif text-[#0A2540] font-bold">
                Compare Options
              </h3>
              <p className="text-base text-[#2C3E50]">
                We present multiple plan options from trusted providers, explaining benefits and costs clearly.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 text-center space-y-4">
              <div className="text-5xl font-bold text-[#0B8FD8]">3</div>
              <h3 className="text-xl font-serif text-[#0A2540] font-bold">
                Enroll & Support
              </h3>
              <p className="text-base text-[#2C3E50]">
                We guide you through enrollment and provide ongoing support for any questions or changes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#0B8FD8] py-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-white font-bold mb-6">
            Get the Coverage You Deserve
          </h2>
          <p className="text-lg text-white mb-8">
            Don't let healthcare gaps leave you vulnerable. Schedule your free consultation today 
            and find the perfect supplement plan for your needs.
          </p>
          <Button 
            className="bg-white hover:bg-gray-100 text-[#0B8FD8] px-8 py-4 text-lg rounded-md transition-all duration-300 shadow-lg hover:shadow-xl"
            onClick={() => window.open('https://go.sbrooksassociatesllc.com/bookwithshannon', '_blank')}
          >
            Schedule Your Free Consultation
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default SupplementPlans;
